"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PayloadEncoder = exports.ZRX_GAZ_MULTIPLIER = exports.NULL_ADDRESS = void 0;
const web3Coder = require('web3-eth-abi');
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const cofix_1 = require("./dexs/cofix");
const types_1 = require("../types");
const zerox_1 = require("./dexs/zerox");
const oasis_1 = require("./dexs/oasis");
const kyber_1 = require("./dexs/kyber");
const constants_1 = require("../constants");
exports.NULL_ADDRESS = '0x0000000000000000000000000000000000000000';
exports.ZRX_GAZ_MULTIPLIER = 70000;
const LENDING_DEXES = ['compound', 'Fulcrum', 'idle', 'aave', 'aave2'];
class PayloadEncoder {
    constructor(network, web3Provider, dexConf, tokens, augustusVersion) {
        this.network = network;
        this.web3Provider = web3Provider;
        this.dexConf = dexConf;
        this.tokens = tokens;
        this.augustusVersion = augustusVersion;
        this.multiSwapSteps = (priceRoute) => {
            return (priceRoute.multiRoute && priceRoute.multiRoute.length) || 1;
        };
        this.getBuyPath = (srcToken, destToken, priceRoute, gasPrice, slippageFactor) => __awaiter(this, void 0, void 0, function* () {
            const routes = yield Promise.all(priceRoute.bestRoute.map((route) => __awaiter(this, void 0, void 0, function* () { return yield this.getBuyRouteParams(route, gasPrice, slippageFactor); })));
            return [
                {
                    to: destToken,
                    totalNetworkFee: this.getTotalNetworkFee(routes),
                    routes,
                },
            ];
        });
        this.getSellRouteParams = (route, gasPrice) => __awaiter(this, void 0, void 0, function* () {
            const { tokenFrom, tokenTo } = route.data;
            const exchangeName = route.exchange.toLowerCase();
            const networkFee = this.networkFee(exchangeName, tokenFrom, tokenTo, gasPrice, route.data);
            const payload = yield this.getPayLoad(tokenFrom, tokenTo, exchangeName, route.data, networkFee, constants_1.SwapSide.SELL);
            const targetExchange = this.getTargetExchange(tokenFrom, exchangeName, route.data.exchange);
            return {
                exchange: this.dexConf[exchangeName].exchange,
                targetExchange,
                percent: new bignumber_js_1.default(route.percent).times(100).toFixed(0),
                payload,
                networkFee,
            };
        });
        this.getBuyRouteParams = (route, gasPrice, slippageFactor) => __awaiter(this, void 0, void 0, function* () {
            const { tokenFrom, tokenTo } = route.data;
            const exchangeName = route.exchange.toLowerCase();
            const networkFee = this.networkFee(exchangeName, tokenFrom, tokenTo, gasPrice, route.data);
            const payload = yield this.getPayLoad(tokenFrom, tokenTo, exchangeName, route.data, networkFee, constants_1.SwapSide.BUY);
            const targetExchange = this.getTargetExchange(tokenFrom, exchangeName, route.data.exchange);
            return {
                exchange: this.dexConf[exchangeName].exchange,
                targetExchange,
                fromAmount: this.applySlippageForBuy(exchangeName)
                    ? new bignumber_js_1.default(route.srcAmount)
                        .times(slippageFactor)
                        .toFixed(0, bignumber_js_1.default.ROUND_DOWN)
                    : route.srcAmount,
                toAmount: route.destAmount,
                payload,
                networkFee,
            };
        });
        this.encodeSellMegaPath = (route, gasPrice) => __awaiter(this, void 0, void 0, function* () {
            return Promise.all(route.map((route) => __awaiter(this, void 0, void 0, function* () {
                return ({
                    fromAmountPercent: new bignumber_js_1.default(route.percent).times(100).toFixed(0),
                    path: yield this.encodeSellPath(route.route, gasPrice),
                });
            })));
        });
        this.encodeSellPath = (path, gasPrice) => __awaiter(this, void 0, void 0, function* () {
            return Promise.all(path.map((swap) => __awaiter(this, void 0, void 0, function* () {
                const encodedSwap = yield this.encodeSellSwap(swap, gasPrice);
                return {
                    to: swap[0].data.tokenTo,
                    totalNetworkFee: this.getTotalNetworkFee(encodedSwap),
                    routes: encodedSwap,
                };
            })));
        });
        this.encodeSellSwap = (swap, gasPrice) => __awaiter(this, void 0, void 0, function* () {
            return Promise.all(swap.map((rate) => __awaiter(this, void 0, void 0, function* () { return yield this.getSellRouteParams(rate, gasPrice); })));
        });
        this.getSellMegaPath = (srcToken, destToken, priceRoute, gasPrice) => __awaiter(this, void 0, void 0, function* () {
            const { multiRoute, megaRoute, bestRoute } = priceRoute;
            const isGiantPath = megaRoute && megaRoute.length !== 0;
            const isMultiPath = multiRoute && multiRoute.length !== 0;
            return isGiantPath
                ? this.encodeSellMegaPath(megaRoute, gasPrice)
                : this.encodeSellMegaPath([
                    {
                        percent: '100',
                        route: isMultiPath ? multiRoute : [bestRoute],
                    },
                ], gasPrice);
        });
        this.getSellPath = (srcToken, destToken, priceRoute, gasPrice) => __awaiter(this, void 0, void 0, function* () {
            const { multiRoute, bestRoute } = priceRoute;
            const isMultiPath = this.multiSwapSteps(priceRoute) > 1;
            return this.encodeSellPath(isMultiPath ? multiRoute : [bestRoute], gasPrice);
        });
        this.networkFee = (exchange, srcToken, destToken, gasPrice, payload) => {
            switch (exchange.toLowerCase()) {
                case '0x':
                case '0xrfqt':
                    return new bignumber_js_1.default(gasPrice)
                        .times(exports.ZRX_GAZ_MULTIPLIER)
                        .times(payload.orders.length)
                        .toFixed();
                case 'cofix':
                    return new cofix_1.Cofix(this.network, this.web3Provider).getNetworkFees(srcToken, destToken);
                default:
                    return '0';
            }
        };
        this.getTargetExchange = (tokenFrom, exchangeName, exchangeAddress) => {
            if (LENDING_DEXES.find(d => d.toString().toLowerCase() === exchangeName.toLowerCase())) {
                return tokenFrom;
            }
            const _poolSpecificExchanges = [
                'swerve',
                'shell',
                'acryptos',
                'beltfi',
                'curve',
                'ellipsis',
                'nerve',
                'saddle',
                'oneinchlp',
            ];
            if (exchangeName.toLowerCase().match(/^curve(.*)/) ||
                _poolSpecificExchanges.includes(exchangeName.toLowerCase())) {
                return exchangeAddress;
            }
            return (this.dexConf[exchangeName.toLowerCase()].targetExchange || exports.NULL_ADDRESS);
        };
        this.getPayLoad = (fromToken, toToken, exchange, data, networkFee, side) => __awaiter(this, void 0, void 0, function* () {
            const srcToken = this.tokens.find(t => t.address.toLowerCase() === fromToken.toLowerCase());
            const destToken = this.tokens.find(t => t.address.toLowerCase() === toToken.toLowerCase());
            if (exchange.toLowerCase().match(/^paraswappool(.*)/)) {
                return data.version === 4
                    ? web3Coder.encodeParameter({
                        ParentStruct: {
                            order: {
                                makerToken: 'address',
                                takerToken: 'address',
                                makerAmount: 'uint128',
                                takerAmount: 'uint128',
                                maker: 'address',
                                taker: 'address',
                                txOrigin: 'address',
                                pool: 'bytes32',
                                expiry: 'uint64',
                                salt: 'uint256',
                            },
                            signature: {
                                signatureType: 'uint8',
                                v: 'uint8',
                                r: 'bytes32',
                                s: 'bytes32',
                            },
                        },
                    }, {
                        order: zerox_1.ZeroXOrder.formatOrders(data.orders, 4)[0],
                        signature: data.orders[0].signature,
                    })
                    : web3Coder.encodeParameter({
                        ParentStruct: {
                            'orders[]': {
                                makerAddress: 'address',
                                takerAddress: 'address',
                                feeRecipientAddress: 'address',
                                senderAddress: 'address',
                                makerAssetAmount: 'uint256',
                                takerAssetAmount: 'uint256',
                                makerFee: 'uint256',
                                takerFee: 'uint256',
                                expirationTimeSeconds: 'uint256',
                                salt: 'uint256',
                                makerAssetData: 'bytes',
                                takerAssetData: 'bytes',
                            },
                            signatures: 'bytes[]',
                        },
                    }, {
                        orders: zerox_1.ZeroXOrder.formatOrders(data.orders),
                        signatures: data.orders.map((o) => o.signature),
                    });
            }
            switch (exchange.toLowerCase()) {
                case '0x':
                case '0xrfqt': {
                    if (side == constants_1.SwapSide.BUY)
                        return '0x';
                    const orderData = zerox_1.ZeroXOrder.formatOrders(data.orders, 3);
                    const signatures = data.orders.map((o) => o.signature);
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            'orders[]': {
                                makerAddress: 'address',
                                takerAddress: 'address',
                                feeRecipientAddress: 'address',
                                senderAddress: 'address',
                                makerAssetAmount: 'uint256',
                                takerAssetAmount: 'uint256',
                                makerFee: 'uint256',
                                takerFee: 'uint256',
                                expirationTimeSeconds: 'uint256',
                                salt: 'uint256',
                                makerAssetData: 'bytes',
                                takerAssetData: 'bytes',
                                makerFeeAssetData: 'bytes',
                                takerFeeAssetData: 'bytes',
                            },
                            signatures: 'bytes[]',
                            networkFee: 'uint256',
                        },
                    }, {
                        orders: orderData,
                        signatures: signatures,
                        networkFee,
                    });
                }
                case 'oasis': {
                    const { otc, weth, factory } = oasis_1.Oasis.getExchangeParams(this.network);
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            otc: 'address',
                            weth: 'address',
                            factory: 'address',
                        },
                    }, { otc, weth, factory });
                }
                case 'kyber': {
                    const kyber = new kyber_1.Kyber(this.network, this.web3Provider);
                    const hint = yield kyber.buildHint(fromToken, toToken);
                    const minConversionRateForBuy = 1;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            minConversionRateForBuy: 'uint256',
                            hint: 'bytes',
                        },
                    }, { minConversionRateForBuy, hint });
                }
                case 'bancor': {
                    if (side == constants_1.SwapSide.BUY)
                        return '0x';
                    const { path } = data;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            path: 'address[]',
                        },
                    }, { path });
                }
                case 'balancer': {
                    const { swaps } = data;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            'swaps[]': {
                                pool: 'address',
                                tokenInParam: 'uint',
                                tokenOutParam: 'uint',
                                maxPrice: 'uint',
                            },
                        },
                    }, { swaps });
                }
                case 'dodov1': {
                    const { dodoPairs, directions } = data;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            dodoPairs: 'address[]',
                            directions: 'uint256',
                        },
                    }, { dodoPairs, directions });
                }
                case 'dodov2': {
                    const { dodoPairs, directions } = data;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            dodoPairs: 'address[]',
                            directions: 'uint256',
                        },
                    }, { dodoPairs, directions });
                }
                case 'compound': {
                    const cToken = destToken.tokenType === 'cToken'
                        ? destToken.address
                        : srcToken.address;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            cToken: 'address',
                        },
                    }, { cToken });
                }
                case 'aave2': {
                    const aToken2 = destToken.tokenType === 'aToken2'
                        ? destToken.address
                        : srcToken.address;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            aToken: 'address',
                        },
                    }, { aToken: aToken2 });
                }
                case 'aave': {
                    const aToken = destToken.tokenType === 'aToken'
                        ? destToken.address
                        : srcToken.address;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            aToken: 'address',
                        },
                    }, { aToken });
                }
                case 'idle': {
                    const idleToken = destToken.tokenType === 'idleToken'
                        ? destToken.address
                        : srcToken.address;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            idleToken: 'address',
                        },
                    }, { idleToken });
                }
                case 'fulcrum': {
                    const iToken = destToken.tokenType === 'iToken'
                        ? destToken.address
                        : srcToken.address;
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            iToken: 'address',
                        },
                    }, { iToken });
                }
                case 'uniswapv2':
                case 'sushiswap':
                case 'defiswap':
                case 'linkswap':
                case 'pancakeswap':
                case 'pancakeswapv2':
                case 'apeswap':
                case 'bakeryswap':
                case 'julswap':
                case 'streetswap':
                case 'quickswap':
                case 'cometh':
                case 'dfyn':
                case 'pantherswap': {
                    const _path = this.augustusVersion === constants_1.AugustusVersion.v2
                        ? data.path
                        : data.path.map((token, i) => {
                            if ((i === 0 &&
                                fromToken.toLowerCase() === types_1.ETHER_ADDRESS.toLowerCase()) ||
                                (i === data.path.length - 1 &&
                                    toToken.toLowerCase() === types_1.ETHER_ADDRESS.toLowerCase())) {
                                return types_1.ETHER_ADDRESS;
                            }
                            return token;
                        });
                    return web3Coder.encodeParameter({
                        ParentStruct: {
                            path: 'address[]',
                        },
                    }, { path: _path });
                }
                case 'curve3':
                case 'swerve':
                case 'curve':
                case 'acryptos':
                case 'beltfi':
                case 'ellipsis': {
                    try {
                        const { i, j, deadline, underlyingSwap, v3 } = data;
                        return web3Coder.encodeParameter({
                            ParentStruct: {
                                i: 'int128',
                                j: 'int128',
                                deadline: 'uint256',
                                underlyingSwap: 'bool',
                                v3: 'bool',
                            },
                        }, { i, j, deadline, underlyingSwap, v3 });
                    }
                    catch (e) {
                        console.error('Curve Error', e);
                        return '0x';
                    }
                }
                case 'saddle':
                case 'nerve': {
                    try {
                        const { i, j, deadline } = data;
                        return web3Coder.encodeParameter({
                            ParentStruct: {
                                i: 'int128',
                                j: 'int128',
                                deadline: 'uint256',
                            },
                        }, { i, j, deadline });
                    }
                    catch (e) {
                        console.error('Nerve Error', e);
                        return '0x';
                    }
                }
                case 'uniswapv3': {
                    try {
                        const { fee, deadline, sqrtPriceLimitX96 } = data;
                        return web3Coder.encodeParameter({
                            ParentStruct: {
                                fee: 'uint24',
                                deadline: 'uint256',
                                sqrtPriceLimitX96: 'uint160',
                            },
                        }, {
                            fee,
                            deadline: deadline || Math.floor(new Date().getTime() / 1000) + 60 * 60,
                            sqrtPriceLimitX96: sqrtPriceLimitX96 || 0,
                        });
                    }
                    catch (e) {
                        console.error('uniswapv3 Error', e);
                        return '0x';
                    }
                }
                case 'smoothy': {
                    try {
                        const { i, j } = data;
                        return web3Coder.encodeParameter({
                            ParentStruct: {
                                i: 'uint256',
                                j: 'uint256',
                            },
                        }, {
                            i,
                            j,
                        });
                    }
                    catch (e) {
                        console.error('smoothy Error', e);
                        return '0x';
                    }
                }
                default:
                    return '0x';
            }
        });
    }
    getTotalNetworkFee(routes) {
        return routes.reduce((acc, route) => {
            acc = new bignumber_js_1.default(acc).plus(route.networkFee || '0').toFixed(0);
            return acc;
        }, '0');
    }
    applySlippageForBuy(exchange) {
        switch (exchange.toLowerCase()) {
            case 'oasis':
            case 'kyber':
            case 'balancer':
            case 'uniswap':
            case 'uniswapv2':
            case 'sushiswap':
            case 'defiswap':
            case 'linkswap':
            case 'shell':
            case 'pancakeswap':
            case 'pancakeswapv2':
            case 'apeswap':
            case 'bakeryswap':
            case 'julswap':
            case 'streetswap':
            case 'quickswap':
            case 'cometh':
            case 'uniswapv3':
            case 'dfyn':
            case 'pantherswap':
                return true;
            /*
             * 0x(v2/v3), 0xrfqt, paraswappool, paraswappool2, compound, aave, idle,
             * fulcrum (bzx), chai, weth, bdai, beth
             * Not supported for buy: bancor, curve, swerve, cofix
             */
            default:
                return false;
        }
    }
}
exports.PayloadEncoder = PayloadEncoder;
//# sourceMappingURL=payload-encoder.js.map